import {
  Divider,
  Drawer,
  Input,
  InputNumber,
  Select,
  Tag,
  Tooltip,
  notification,
} from "antd";
import classNames from "classnames";
import { cloneDeep, get, merge } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  BiCheckSquare,
  BiChevronLeft,
  BiCog,
  BiData,
  BiDownload,
  BiHighlight,
  BiPen,
  BiSearch,
  BiText,
  BiUpload,
  BiZoomIn,
  BiZoomOut,
} from "react-icons/bi";
import { useTranslation } from "react-i18next";
import Button from "shared/components/Button";
import Card from "shared/components/Card";
import ConditionsEditor from "shared/components/ConditionsEditor";
import PayloadPathSelect, {
  usePayloadPaths,
} from "shared/components/PayloadPaths";
import SourceEditor, { getSourceMode } from "shared/components/SourceEditor";
import TransformsEditor from "shared/components/TransformsEditor";
import { NAME_SPACES } from "shared/locales/constants";
import {
  getChildLoopGroupKey,
  getChildLoopIndex,
  isCheckboxAnchor,
  isChildLoopKey,
  isSkipAnchor,
} from "../../anchors";
import {
  buildConfigExport,
  configFileName,
  diffConfigAgainstAnchors,
  parseConfigImport,
} from "../../config-io";
import { PART_MODE_OPTIONS, SOURCE_MODES } from "../constants";

const buildNestedPayload = (flatValues) => {
  const result = {};
  Object.entries(flatValues).forEach(([path, value]) => {
    if (!path || value === "") return;
    const keys = path.split(".");
    let cur = result;
    for (let i = 0; i < keys.length - 1; i++) {
      if (!cur[keys[i]] || typeof cur[keys[i]] !== "object") cur[keys[i]] = {};
      cur = cur[keys[i]];
    }
    cur[keys[keys.length - 1]] = value;
  });
  return result;
};

const isChildLoopMapping = (mapping) => mapping?.type === "childLoop";

const getCheckboxBase = (anchor) => {
  const m = anchor.match(/^\[([A-Z]+)/);
  return m ? m[1] : null;
};
const isSpreadMapping = (mapping) => mapping?.type === "spread";
const isDentalMapping = (mapping) => mapping?.type === "dental";
const isStaticMapping = (mapping) => mapping?.type === "static";

const hasSource = (mapping = {}) => {
  const mode = getSourceMode(mapping);
  if (mode === SOURCE_MODES.KEYS)
    return (mapping.keys || []).some((key) => !!key);
  if (mode === SOURCE_MODES.FROM) return !!mapping.from?.path;
  if (mode === SOURCE_MODES.STATIC) return true;
  if (mode === SOURCE_MODES.COMBINE)
    return (mapping.combine?.parts || []).some(hasSource);
  return !!mapping.key;
};

const StepAnchors = ({
  anchors: rawAnchors,
  mappings,
  onMappingChange,
  onNext,
  onBack,
  onPreview,
  previewing,
  file,
  unresolved,
  schemaLoading,
  onLoadPayloadSchema,
  schemaPayload,
  templateName,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.DYNAMIC_DOCUMENTS);
  const ANCHORS = t("ANCHORS", { returnObjects: true });
  const CONFIG_IO = t("CONFIG_IO", { returnObjects: true });
  const SOURCE_EDITOR = t(`${NAME_SPACES.COMPONENTS}:SOURCE_EDITOR`, {
    returnObjects: true,
  });
  const payloadPaths = usePayloadPaths();
  const anchors = rawAnchors.filter((a, i) => rawAnchors.indexOf(a) === i);
  const [pdfUrl, setPdfUrl] = useState(null);

  const [configAnchor, setConfigAnchor] = useState(null);
  const [configCheckboxBase, setConfigCheckboxBase] = useState(null);

  const [payloadOpen, setPayloadOpen] = useState(false);
  const [mockValues, setMockValues] = useState({});
  const [schemaSubscriptionId, setSchemaSubscriptionId] = useState("");
  const configInputRef = useRef(null);

  const mappedKeys = useMemo(() => {
    const keys = new Set();
    const walk = (source) => {
      if (!source || typeof source !== "object") return;
      if (source.key) keys.add(source.key);
      (source.keys || []).forEach((key) => key && keys.add(key));
      if (source.from?.path) keys.add(source.from.path);
      (source.combine?.parts || []).forEach(walk);
      (source.conditions?.rules || []).forEach((rule) => {
        if (rule.key) keys.add(rule.key);
      });
    };
    Object.values(mappings).forEach(walk);
    return [...keys];
  }, [mappings]);

  const setMockValue = (path, value) =>
    setMockValues((prev) => ({ ...prev, [path]: value }));

  const payloadValueAt = (path) => {
    if (!schemaPayload) return "";
    const value = get(schemaPayload, path.replace(/\[N\]/g, "[0]"));
    if (value === undefined || value === null) return "";
    return typeof value === "object" ? "" : String(value);
  };

  const buildPreviewPayload = () => {
    const overrides = buildNestedPayload(mockValues);
    if (!schemaPayload) return overrides;
    return merge(cloneDeep(schemaPayload), overrides);
  };

  const handleExportConfig = () => {
    const blob = new Blob(
      [
        JSON.stringify(
          buildConfigExport({
            name: templateName,
            anchors,
            anchorMappings: mappings,
          }),
          null,
          2,
        ),
      ],
      { type: "application/json;charset=utf-8" },
    );
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = configFileName(templateName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleImportConfig = (event) => {
    const selected = event.target.files?.[0];
    event.target.value = "";
    if (!selected) return;

    const reader = new FileReader();
    reader.onload = () => {
      let imported;
      try {
        imported = parseConfigImport(String(reader.result));
      } catch (error) {
        notification.error({
          message: CONFIG_IO.ERROR,
          description:
            CONFIG_IO.ERRORS[error.code] ||
            t(`CONFIG_IO.ERRORS.${error.code}`, {
              anchors: (error.anchors || []).join(", "),
            }),
        });
        return;
      }

      onMappingChange(imported.anchorMappings);

      const { uncovered, unknown } = diffConfigAgainstAnchors(
        imported.anchorMappings,
        anchors,
      );
      const warnings = [
        uncovered.length > 0 &&
          t("CONFIG_IO.UNCOVERED", {
            count: uncovered.length,
            anchors: uncovered.join(", "),
          }),
        unknown.length > 0 &&
          t("CONFIG_IO.UNKNOWN", {
            count: unknown.length,
            anchors: unknown.join(", "),
          }),
      ].filter(Boolean);

      const description = [
        t("CONFIG_IO.IMPORTED_DESCRIPTION", {
          count: Object.keys(imported.anchorMappings).length,
        }),
        ...warnings,
      ].join(" ");

      if (warnings.length > 0)
        notification.warning({ message: CONFIG_IO.IMPORTED, description });
      else notification.success({ message: CONFIG_IO.IMPORTED, description });
    };
    reader.readAsText(selected);
  };

  useEffect(() => {
    if (!file?.content) {
      setPdfUrl(null);
      return undefined;
    }

    const bytes = Uint8Array.from(atob(file.content), (c) => c.charCodeAt(0));
    const blob = new Blob([bytes], {
      type: file.contentType || "application/pdf",
    });
    const url = URL.createObjectURL(blob);
    setPdfUrl(url);

    return () => URL.revokeObjectURL(url);
  }, [file]);

  const setField = (anchor, field, value) => {
    const current = mappings[anchor] || {};
    onMappingChange({ ...mappings, [anchor]: { ...current, [field]: value } });
  };

  const clearAnchor = (anchor) => {
    const next = { ...mappings };
    delete next[anchor];
    onMappingChange(next);
  };

  const switchType = (anchor, newType) => {
    const existing = mappings[anchor] || {};
    const { key, keys, from, combine, transforms } = existing;
    const source = { key, keys, from, combine, transforms };
    if (newType === "spread") {
      onMappingChange({
        ...mappings,
        [anchor]: {
          ...source,
          type: "spread",
          fontSize: existing.fontSize,
          start: existing.start,
          end: existing.end,
        },
      });
    } else if (newType === "dental") {
      onMappingChange({
        ...mappings,
        [anchor]: {
          ...source,
          type: "dental",
          schema: existing.schema || "",
          fontSize: existing.fontSize,
        },
      });
    } else if (newType === "static") {
      onMappingChange({
        ...mappings,
        [anchor]: {
          type: "static",
          staticValue: existing.staticValue || "",
        },
      });
    } else {
      onMappingChange({ ...mappings, [anchor]: { ...source } });
    }
  };

  const allMapped = anchors.every((a) => {
    if (isCheckboxAnchor(a)) return true;
    if (isSkipAnchor(a)) return true;
    const gk = getChildLoopGroupKey(a);
    if (gk) return hasSource(mappings[gk]);
    const m = mappings[a];
    if (isStaticMapping(m)) return true;
    if (isDentalMapping(m)) return hasSource(m) && !!m?.schema;
    return hasSource(m);
  });

  const displayItems = useMemo(() => {
    const items = [];
    const seenCheckboxBases = new Set();
    const seenChildGroups = new Set();

    anchors.forEach((anchor) => {
      if (isCheckboxAnchor(anchor)) {
        const base = getCheckboxBase(anchor);
        if (!seenCheckboxBases.has(base)) {
          seenCheckboxBases.add(base);
          const variants = anchors
            .filter((a) => isCheckboxAnchor(a) && getCheckboxBase(a) === base)
            .sort((a, b) => a.length - b.length || a.localeCompare(b));
          items.push({ type: "checkboxGroup", base, variants });
        }
      } else {
        const groupKey = getChildLoopGroupKey(anchor);
        if (groupKey) {
          if (!seenChildGroups.has(groupKey)) {
            seenChildGroups.add(groupKey);
            const variants = anchors
              .filter((a) => getChildLoopGroupKey(a) === groupKey)
              .sort((a, b) => getChildLoopIndex(a) - getChildLoopIndex(b));
            items.push({ type: "childLoop", groupKey, variants });
          }
        } else {
          items.push({ type: "anchor", anchor });
        }
      }
    });
    return items;
  }, [anchors]);

  useEffect(() => {
    const childLoopItems = displayItems.filter((it) => it.type === "childLoop");
    const neededKeys = new Set(childLoopItems.map((it) => it.groupKey));
    let update = { ...mappings };
    let changed = false;
    childLoopItems.forEach(({ groupKey }) => {
      if (!update[groupKey]) {
        update[groupKey] = { type: "childLoop" };
        changed = true;
      }
    });
    Object.keys(update).forEach((k) => {
      if (isChildLoopKey(k) && !neededKeys.has(k)) {
        delete update[k];
        changed = true;
      }
    });
    if (changed) onMappingChange(update);
  }, [displayItems]); // eslint-disable-line react-hooks/exhaustive-deps

  const cfgMapping = configAnchor ? mappings[configAnchor] || {} : {};
  const cfgIsCheckbox = configAnchor ? isCheckboxAnchor(configAnchor) : false;
  const cfgIsDental = !cfgIsCheckbox && isDentalMapping(cfgMapping);
  const cfgIsSpread =
    !cfgIsCheckbox && !cfgIsDental && isSpreadMapping(cfgMapping);
  const cfgIsStatic =
    !cfgIsCheckbox &&
    !cfgIsDental &&
    !cfgIsSpread &&
    isStaticMapping(cfgMapping);
  const cfgIsChildLoop = !cfgIsCheckbox && isChildLoopMapping(cfgMapping);
  const cfgIsText =
    !cfgIsCheckbox &&
    !cfgIsDental &&
    !cfgIsSpread &&
    !cfgIsStatic &&
    !cfgIsChildLoop;
  const cfgKeyMissing =
    !cfgIsCheckbox && !cfgIsStatic && !hasSource(cfgMapping);

  const hasConfig = (anchor) => {
    const m = mappings[anchor] || {};
    if (isCheckboxAnchor(anchor))
      return !!(m.width || m.height || m.color || m.conditions?.rules?.length);
    const rich =
      m.transforms?.length ||
      getSourceMode(m) !== SOURCE_MODES.KEY ||
      m.conditions?.rules?.length;
    if (isDentalMapping(m) || isSpreadMapping(m))
      return !!(m.fontSize || m.start || m.end || m.schema || rich);
    return !!(m.letterSpacing || m.start || m.end || rich);
  };

  const getConditions = (anchor) =>
    mappings[anchor]?.conditions || { operator: "AND", rules: [] };

  const setConditions = (anchor, conditions) =>
    setField(anchor, "conditions", conditions);

  const addRule = (anchor) => {
    const cond = getConditions(anchor);
    setConditions(anchor, {
      ...cond,
      rules: [...cond.rules, { key: "", op: "equals", value: "" }],
    });
  };

  const removeRule = (anchor, idx) => {
    const cond = getConditions(anchor);
    const rules = cond.rules.filter((_, i) => i !== idx);
    setConditions(anchor, { ...cond, rules });
  };

  const updateRule = (anchor, idx, patch) => {
    const cond = getConditions(anchor);
    const rules = cond.rules.map((r, i) =>
      i === idx ? { ...r, ...patch } : r,
    );
    setConditions(anchor, { ...cond, rules });
  };

  const setConditionOperator = (anchor, operator) => {
    const cond = getConditions(anchor);
    setConditions(anchor, { ...cond, operator });
  };

  const pipeIdx_ = (anchor) => anchor.indexOf("|");

  return (
    <Card type="simple">
      <div className="pricers-import__card-header">
        <div>
          <h3 className="pricers-import__card-title">{ANCHORS.TITLE}</h3>
          <p className="pricers-import__card-description">
            {t("ANCHORS.DESCRIPTION", { count: anchors.length })}
          </p>
        </div>
        <Button type="primary--outlined" onClick={onBack}>
          <BiChevronLeft size={18} />
          {ANCHORS.BACK}
        </Button>
      </div>

      <div className="pricers-import__card-body">
        <div className={file ? "pricers-import__document-editor" : undefined}>
          {file && (
            <div className="pricers-import__document-canvas">
              <div className="pricers-import__document-toolbar">
                <Tooltip title={ANCHORS.TOOLBAR.SELECTION}>
                  <button
                    type="button"
                    className="pricers-import__document-tool pricers-import__document-tool--active"
                  >
                    <BiSearch size={18} />
                  </button>
                </Tooltip>
                <Tooltip title={ANCHORS.TOOLBAR.TEXT}>
                  <button
                    type="button"
                    className="pricers-import__document-tool"
                  >
                    <BiText size={18} />
                  </button>
                </Tooltip>
                <Tooltip title={ANCHORS.TOOLBAR.SIGNATURE}>
                  <button
                    type="button"
                    className="pricers-import__document-tool"
                  >
                    <BiPen size={18} />
                  </button>
                </Tooltip>
                <Tooltip title={ANCHORS.TOOLBAR.CHECKBOX}>
                  <button
                    type="button"
                    className="pricers-import__document-tool"
                  >
                    <BiCheckSquare size={18} />
                  </button>
                </Tooltip>
                <Tooltip title={ANCHORS.TOOLBAR.HIGHLIGHT}>
                  <button
                    type="button"
                    className="pricers-import__document-tool"
                  >
                    <BiHighlight size={18} />
                  </button>
                </Tooltip>
                <div className="pricers-import__document-toolbar-spacer" />
                <Tooltip title={ANCHORS.TOOLBAR.ZOOM_OUT}>
                  <button
                    type="button"
                    className="pricers-import__document-tool"
                  >
                    <BiZoomOut size={18} />
                  </button>
                </Tooltip>
                <Tooltip title={ANCHORS.TOOLBAR.ZOOM_IN}>
                  <button
                    type="button"
                    className="pricers-import__document-tool"
                  >
                    <BiZoomIn size={18} />
                  </button>
                </Tooltip>
              </div>

              <div className="pricers-import__document-preview">
                {pdfUrl ? (
                  <iframe
                    src={pdfUrl}
                    title={ANCHORS.TOOLBAR.DOCUMENT}
                    className="pricers-import__document-frame"
                  />
                ) : (
                  <div className="pricers-import__document-empty">
                    {ANCHORS.TOOLBAR.PREVIEW_UNAVAILABLE}
                  </div>
                )}
              </div>

              <div className="pricers-import__document-status">
                <span>{file.name}</span>
                <span>
                  {t("ANCHORS.TOOLBAR.ANCHOR_COUNT", { count: anchors.length })}
                </span>
              </div>
            </div>
          )}

          <div className={file ? "pricers-import__document-side" : undefined}>
            {file && (
              <div className="pricers-import__document-side-header">
                <div>
                  <h4>{ANCHORS.SIDE.TITLE}</h4>
                  <p>{ANCHORS.SIDE.DESCRIPTION}</p>
                </div>
              </div>
            )}
            {unresolved?.length > 0 && (
              <div className="dynamic-documents__unresolved">
                <strong>
                  {t("ANCHORS.UNRESOLVED.TITLE", { count: unresolved.length })}
                </strong>
                <p>{ANCHORS.UNRESOLVED.DESCRIPTION}</p>
                <ul>
                  {unresolved.map((path) => (
                    <li key={path}>{path}</li>
                  ))}
                </ul>
              </div>
            )}
            {anchors.length === 0 ? (
              <p className="dynamic-documents__empty-message">
                {ANCHORS.EMPTY}
              </p>
            ) : (
              <table className="dynamic-documents__anchors-table">
                <thead>
                  <tr>
                    <th className="dynamic-documents__anchors-th dynamic-documents__anchors-th--anchor">
                      {ANCHORS.TABLE.ANCHOR}
                    </th>
                    <th className="dynamic-documents__anchors-th dynamic-documents__anchors-th--type">
                      {ANCHORS.TABLE.TYPE}
                    </th>
                    <th className="dynamic-documents__anchors-th">
                      {ANCHORS.TABLE.ACCESSOR}
                    </th>
                    <th className="dynamic-documents__anchors-th dynamic-documents__anchors-th--action" />
                  </tr>
                </thead>
                <tbody>
                  {displayItems.map((item) => {
                    if (item.type === "checkboxGroup") {
                      const { base, variants } = item;
                      const groupHasConfig = variants.some((v) => hasConfig(v));
                      return (
                        <tr
                          key={`cbg-${base}`}
                          className="dynamic-documents__anchors-row"
                        >
                          <td className="dynamic-documents__anchors-td">
                            <Tag
                              color="purple"
                              className="dynamic-documents__anchor-tag"
                            >
                              [{base}]
                            </Tag>
                            <div className="dynamic-documents__anchor-meta">
                              {ANCHORS.META.RECTANGLE} ·{" "}
                              {t("ANCHORS.META.OPTION", {
                                count: variants.length,
                              })}
                            </div>
                          </td>
                          <td className="dynamic-documents__anchors-td">
                            <span className="dynamic-documents__placeholder">
                              —
                            </span>
                          </td>
                          <td className="dynamic-documents__anchors-td">
                            <span className="dynamic-documents__placeholder">
                              —
                            </span>
                          </td>
                          <td className="dynamic-documents__anchors-td dynamic-documents__anchors-td--action">
                            <Tooltip title={ANCHORS.TABLE.CHECKBOX_CONFIG}>
                              <button
                                type="button"
                                onClick={() => setConfigCheckboxBase(base)}
                                className={classNames(
                                  "dynamic-documents__config-btn",
                                  {
                                    "dynamic-documents__config-btn--active":
                                      groupHasConfig,
                                  },
                                )}
                              >
                                <BiCog size={18} />
                              </button>
                            </Tooltip>
                          </td>
                        </tr>
                      );
                    }

                    if (item.type === "childLoop") {
                      const { groupKey, variants } = item;
                      const clMapping = mappings[groupKey] || {};
                      return (
                        <tr
                          key={`cl-${groupKey}`}
                          className="dynamic-documents__anchors-row"
                        >
                          <td className="dynamic-documents__anchors-td">
                            <Tag
                              color="geekblue"
                              className="dynamic-documents__anchor-tag"
                            >
                              {`{{${groupKey}}}`}
                            </Tag>
                            <div className="dynamic-documents__anchor-meta">
                              {ANCHORS.META.LOOP} ·{" "}
                              {t("ANCHORS.META.ENTRY", {
                                count: variants.length,
                              })}
                            </div>
                          </td>
                          <td className="dynamic-documents__anchors-td">
                            <Tag
                              color="geekblue"
                              className="dynamic-documents__loop-tag"
                            >
                              {ANCHORS.META.LOOP}
                            </Tag>
                          </td>
                          <td className="dynamic-documents__anchors-td">
                            <PayloadPathSelect
                              value={clMapping.key}
                              onChange={(val) => {
                                const next = { ...mappings };
                                next[groupKey] = val
                                  ? {
                                      ...clMapping,
                                      type: "childLoop",
                                      key: val,
                                    }
                                  : { type: "childLoop" };
                                onMappingChange(next);
                              }}
                            />
                          </td>
                          <td className="dynamic-documents__anchors-td dynamic-documents__anchors-td--action">
                            <Tooltip title={ANCHORS.TABLE.CONFIG}>
                              <button
                                type="button"
                                onClick={() => setConfigAnchor(groupKey)}
                                className={classNames(
                                  "dynamic-documents__config-btn",
                                  {
                                    "dynamic-documents__config-btn--active":
                                      hasConfig(groupKey),
                                  },
                                )}
                              >
                                <BiCog size={18} />
                              </button>
                            </Tooltip>
                          </td>
                        </tr>
                      );
                    }

                    const anchor = item.anchor;
                    const mapping = mappings[anchor] || {};
                    const isCheckbox = isCheckboxAnchor(anchor);
                    const isSkipped = !isCheckbox && isSkipAnchor(anchor);
                    const isDental =
                      !isCheckbox && !isSkipped && isDentalMapping(mapping);
                    const isSpread =
                      !isCheckbox &&
                      !isSkipped &&
                      !isDental &&
                      isSpreadMapping(mapping);
                    const isStatic =
                      !isCheckbox &&
                      !isSkipped &&
                      !isDental &&
                      !isSpread &&
                      isStaticMapping(mapping);

                    const tagColor = isCheckbox
                      ? "purple"
                      : isSkipped
                        ? "volcano"
                        : isDental
                          ? "blue"
                          : isSpread
                            ? "orange"
                            : isStatic
                              ? "green"
                              : undefined;
                    const tagSub = isCheckbox
                      ? ANCHORS.META.RECTANGLE
                      : isSkipped
                        ? ANCHORS.META.SKIPPED
                        : isDental
                          ? ANCHORS.META.DENTAL_GRID
                          : isSpread
                            ? ANCHORS.META.DENTAL
                            : isStatic
                              ? ANCHORS.META.STATIC
                              : null;

                    const currentType = isDental
                      ? "dental"
                      : isSpread
                        ? "spread"
                        : isStatic
                          ? "static"
                          : "text";

                    const pipeIdx = pipeIdx_(anchor);
                    const anchorBase =
                      pipeIdx >= 0 ? anchor.slice(0, pipeIdx) : anchor;
                    const anchorSuffix =
                      pipeIdx >= 0 ? anchor.slice(pipeIdx + 1) : null;

                    const showGear = !isSkipped;
                    const configured = hasConfig(anchor);

                    return (
                      <tr
                        key={anchor}
                        className="dynamic-documents__anchors-row"
                      >
                        <td className="dynamic-documents__anchors-td">
                          <Tag
                            color={tagColor}
                            className="dynamic-documents__anchor-tag"
                          >
                            {isCheckbox ? anchor : `{{${anchorBase}}}`}
                          </Tag>
                          {anchorSuffix && !isSkipped && (
                            <div className="dynamic-documents__anchor-suffix">
                              {ANCHORS.META.SUFFIX}&nbsp;:{" "}
                              <strong>{anchorSuffix}</strong>
                            </div>
                          )}
                          {tagSub && (
                            <div className="dynamic-documents__anchor-meta">
                              {tagSub}
                            </div>
                          )}
                        </td>

                        <td className="dynamic-documents__anchors-td">
                          {isCheckbox || isSkipped ? (
                            <span className="dynamic-documents__placeholder">
                              —
                            </span>
                          ) : (
                            <Select
                              size="small"
                              className="dynamic-documents__field-full"
                              value={currentType}
                              onChange={(val) => switchType(anchor, val)}
                              options={[
                                { label: ANCHORS.TYPES.TEXT, value: "text" },
                                {
                                  label: ANCHORS.TYPES.STATIC,
                                  value: "static",
                                },
                                {
                                  label: ANCHORS.TYPES.SPREAD,
                                  value: "spread",
                                },
                                {
                                  label: ANCHORS.TYPES.DENTAL,
                                  value: "dental",
                                },
                              ]}
                            />
                          )}
                        </td>

                        <td className="dynamic-documents__anchors-td">
                          {isCheckbox || isSkipped ? (
                            <span className="dynamic-documents__placeholder">
                              —
                            </span>
                          ) : isStatic ? (
                            <Input
                              size="small"
                              placeholder={ANCHORS.CONFIG.STATIC_PLACEHOLDER}
                              className="dynamic-documents__field-full"
                              value={mapping.staticValue ?? ""}
                              onChange={(e) =>
                                setField(anchor, "staticValue", e.target.value)
                              }
                            />
                          ) : getSourceMode(mapping) !== SOURCE_MODES.KEY ? (
                            <button
                              type="button"
                              className="dynamic-documents__source-summary"
                              onClick={() => setConfigAnchor(anchor)}
                            >
                              {
                                SOURCE_EDITOR.MODES[
                                  PART_MODE_OPTIONS.find(
                                    (o) => o.value === getSourceMode(mapping),
                                  )?.key
                                ]?.LABEL
                              }
                            </button>
                          ) : (
                            <PayloadPathSelect
                              value={mapping.key}
                              onChange={(val) =>
                                val
                                  ? setField(anchor, "key", val)
                                  : clearAnchor(anchor)
                              }
                            />
                          )}
                        </td>

                        <td className="dynamic-documents__anchors-td dynamic-documents__anchors-td--action">
                          {showGear && (
                            <Tooltip title={ANCHORS.TABLE.CONFIG}>
                              <button
                                type="button"
                                onClick={() => setConfigAnchor(anchor)}
                                className={classNames(
                                  "dynamic-documents__config-btn",
                                  {
                                    "dynamic-documents__config-btn--active":
                                      configured,
                                  },
                                )}
                              >
                                <BiCog size={18} />
                              </button>
                            </Tooltip>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      <Drawer
        open={payloadOpen}
        onClose={() => setPayloadOpen(false)}
        placement="left"
        width={380}
        title={ANCHORS.PAYLOAD_DRAWER.TITLE}
        destroyOnClose={false}
        rootClassName="dynamic-documents__drawer"
      >
        <p className="dynamic-documents__drawer-desc">
          {ANCHORS.PAYLOAD_DRAWER.DESCRIPTION}
        </p>

        {onLoadPayloadSchema && (
          <>
            <div className="dynamic-documents__drawer-field-label">
              {ANCHORS.PAYLOAD_DRAWER.SCHEMA_LABEL}
            </div>
            <Input.Search
              size="small"
              allowClear
              enterButton={ANCHORS.PAYLOAD_DRAWER.SCHEMA_ACTION}
              loading={schemaLoading}
              placeholder={ANCHORS.PAYLOAD_DRAWER.SCHEMA_PLACEHOLDER}
              defaultValue={schemaSubscriptionId}
              onSearch={(value) => {
                setSchemaSubscriptionId(value);
                if (value) onLoadPayloadSchema(value);
              }}
              className="dynamic-documents__field-mono"
            />
            <p className="dynamic-documents__config-field-hint">
              {ANCHORS.PAYLOAD_DRAWER.SCHEMA_HINT}
            </p>
            <Divider className="dynamic-documents__drawer-divider" />
          </>
        )}

        {mappedKeys.length === 0 ? (
          <p className="dynamic-documents__drawer-empty">
            {ANCHORS.PAYLOAD_DRAWER.EMPTY}
          </p>
        ) : (
          <div className="dynamic-documents__drawer-fields">
            {mappedKeys.map((path) => {
              const meta = payloadPaths.find((p) => p.value === path);
              return (
                <div key={path}>
                  <div className="dynamic-documents__drawer-field-label">
                    {meta?.label || path}
                  </div>
                  <Input
                    size="small"
                    placeholder={path}
                    value={mockValues[path] ?? payloadValueAt(path)}
                    onChange={(e) => setMockValue(path, e.target.value)}
                    className="dynamic-documents__field-mono"
                  />
                  {meta && (
                    <div className="dynamic-documents__drawer-field-path">
                      {path}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {mappedKeys.length > 0 && (
          <>
            <Divider className="dynamic-documents__drawer-divider" />
            <button
              type="button"
              onClick={() => setMockValues({})}
              className="dynamic-documents__drawer-reset-btn"
            >
              {ANCHORS.PAYLOAD_DRAWER.RESET}
            </button>
          </>
        )}
      </Drawer>

      <Drawer
        open={!!configAnchor}
        onClose={() => setConfigAnchor(null)}
        width={600}
        title={
          configAnchor ? (
            <span className="dynamic-documents__drawer-title">
              {isCheckboxAnchor(configAnchor)
                ? configAnchor
                : `{{${configAnchor.split("|")[0]}}}`}
            </span>
          ) : null
        }
        destroyOnClose={false}
        rootClassName="dynamic-documents__drawer"
      >
        {configAnchor && (
          <div className="dynamic-documents__drawer-content">
            {!cfgIsCheckbox && !cfgIsStatic && (
              <>
                <SourceEditor
                  mapping={cfgMapping}
                  onChange={(next) =>
                    onMappingChange({ ...mappings, [configAnchor]: next })
                  }
                />
                <TransformsEditor
                  transforms={cfgMapping.transforms || []}
                  onChange={(transforms) =>
                    setField(
                      configAnchor,
                      "transforms",
                      transforms.length > 0 ? transforms : undefined,
                    )
                  }
                />
              </>
            )}

            {cfgIsCheckbox && (
              <>
                <ConfigField label={ANCHORS.CONFIG.WIDTH}>
                  <InputNumber
                    min={1}
                    step={1}
                    precision={0}
                    placeholder="10"
                    className="dynamic-documents__field-full"
                    addonAfter="px"
                    value={cfgMapping.width ?? null}
                    onChange={(val) =>
                      setField(configAnchor, "width", val ?? undefined)
                    }
                  />
                </ConfigField>
                <ConfigField label={ANCHORS.CONFIG.HEIGHT}>
                  <InputNumber
                    min={1}
                    step={1}
                    precision={0}
                    placeholder="10"
                    className="dynamic-documents__field-full"
                    addonAfter="px"
                    value={cfgMapping.height ?? null}
                    onChange={(val) =>
                      setField(configAnchor, "height", val ?? undefined)
                    }
                  />
                </ConfigField>
                <ConfigField label={ANCHORS.CONFIG.FILL_COLOR}>
                  <div className="dynamic-documents__color-row">
                    <input
                      type="color"
                      value={cfgMapping.color || "#000000"}
                      onChange={(e) =>
                        setField(configAnchor, "color", e.target.value)
                      }
                      className="dynamic-documents__color-input"
                    />
                    <span className="dynamic-documents__color-value">
                      {cfgMapping.color || "#000000"}
                    </span>
                  </div>
                </ConfigField>
              </>
            )}

            {/* ── Text config ── */}
            {cfgIsText && (
              <>
                <ConfigField
                  label={ANCHORS.CONFIG.LETTER_SPACING}
                  hint={ANCHORS.CONFIG.LETTER_SPACING_HINT}
                >
                  <InputNumber
                    min={-20}
                    max={40}
                    step={1}
                    precision={1}
                    placeholder="0"
                    className="dynamic-documents__field-full"
                    addonAfter="px"
                    value={cfgMapping.letterSpacing ?? null}
                    onChange={(val) =>
                      setField(configAnchor, "letterSpacing", val ?? undefined)
                    }
                    disabled={cfgKeyMissing}
                  />
                </ConfigField>
                <ConfigField
                  label={ANCHORS.CONFIG.START}
                  hint={ANCHORS.CONFIG.START_HINT}
                >
                  <InputNumber
                    min={0}
                    step={1}
                    precision={0}
                    placeholder="0"
                    className="dynamic-documents__field-full"
                    value={cfgMapping.start ?? null}
                    onChange={(val) =>
                      setField(configAnchor, "start", val ?? undefined)
                    }
                    disabled={cfgKeyMissing}
                  />
                </ConfigField>
                <ConfigField
                  label={ANCHORS.CONFIG.END}
                  hint={ANCHORS.CONFIG.END_HINT}
                >
                  <InputNumber
                    min={1}
                    step={1}
                    precision={0}
                    placeholder={ANCHORS.CONFIG.INFINITY}
                    className="dynamic-documents__field-full"
                    value={cfgMapping.end ?? null}
                    onChange={(val) =>
                      setField(configAnchor, "end", val ?? undefined)
                    }
                    disabled={cfgKeyMissing}
                  />
                </ConfigField>
              </>
            )}

            {cfgIsSpread && (
              <>
                <ConfigField label={ANCHORS.CONFIG.FONT_SIZE}>
                  <InputNumber
                    min={4}
                    max={72}
                    step={1}
                    precision={1}
                    placeholder="9"
                    className="dynamic-documents__field-full"
                    addonAfter="pt"
                    value={cfgMapping.fontSize ?? null}
                    onChange={(val) =>
                      setField(configAnchor, "fontSize", val ?? undefined)
                    }
                    disabled={cfgKeyMissing}
                  />
                </ConfigField>
                <ConfigField label={ANCHORS.CONFIG.SPREAD_START}>
                  <InputNumber
                    min={0}
                    step={1}
                    precision={0}
                    placeholder="0"
                    className="dynamic-documents__field-full"
                    value={cfgMapping.start ?? null}
                    onChange={(val) =>
                      setField(configAnchor, "start", val ?? undefined)
                    }
                    disabled={cfgKeyMissing}
                  />
                </ConfigField>
                <ConfigField label={ANCHORS.CONFIG.SPREAD_END}>
                  <InputNumber
                    min={1}
                    step={1}
                    precision={0}
                    placeholder={ANCHORS.CONFIG.INFINITY}
                    className="dynamic-documents__field-full"
                    value={cfgMapping.end ?? null}
                    onChange={(val) =>
                      setField(configAnchor, "end", val ?? undefined)
                    }
                    disabled={cfgKeyMissing}
                  />
                </ConfigField>
              </>
            )}

            {cfgIsDental && (
              <>
                <ConfigField label={ANCHORS.CONFIG.FONT_SIZE}>
                  <InputNumber
                    min={4}
                    max={72}
                    step={1}
                    precision={1}
                    placeholder="9"
                    className="dynamic-documents__field-full"
                    addonAfter="pt"
                    value={cfgMapping.fontSize ?? null}
                    onChange={(val) =>
                      setField(configAnchor, "fontSize", val ?? undefined)
                    }
                    disabled={cfgKeyMissing}
                  />
                </ConfigField>
                <ConfigField
                  label={ANCHORS.CONFIG.SCHEMA}
                  hint={ANCHORS.CONFIG.SCHEMA_HINT}
                >
                  <Input
                    placeholder={ANCHORS.CONFIG.SCHEMA_PLACEHOLDER}
                    className="dynamic-documents__field-full dynamic-documents__field-mono"
                    value={cfgMapping.schema ?? ""}
                    onChange={(e) =>
                      setField(
                        configAnchor,
                        "schema",
                        e.target.value || undefined,
                      )
                    }
                    disabled={cfgKeyMissing}
                  />
                </ConfigField>
              </>
            )}

            {cfgIsChildLoop && (
              <ConfigField
                label={ANCHORS.CONFIG.ITEM_KEY}
                hint={ANCHORS.CONFIG.ITEM_KEY_HINT}
              >
                <Input
                  placeholder={ANCHORS.CONFIG.ITEM_KEY_PLACEHOLDER}
                  className="dynamic-documents__field-full dynamic-documents__field-mono"
                  value={cfgMapping.itemKey ?? ""}
                  onChange={(e) =>
                    setField(
                      configAnchor,
                      "itemKey",
                      e.target.value || undefined,
                    )
                  }
                />
              </ConfigField>
            )}

            {cfgIsStatic && (
              <p className="dynamic-documents__drawer-static-msg">
                {ANCHORS.CONFIG.STATIC_MESSAGE}
              </p>
            )}

            <ConditionsEditor
              conditions={getConditions(configAnchor)}
              emptyMessage={ANCHORS.CONFIG.CONDITIONS_EMPTY}
              onOperatorChange={(op) => setConditionOperator(configAnchor, op)}
              onAddRule={() => addRule(configAnchor)}
              onRemoveRule={(idx) => removeRule(configAnchor, idx)}
              onUpdateRule={(idx, patch) =>
                updateRule(configAnchor, idx, patch)
              }
            />
          </div>
        )}
      </Drawer>

      <Drawer
        open={!!configCheckboxBase}
        onClose={() => setConfigCheckboxBase(null)}
        width={400}
        title={
          configCheckboxBase ? (
            <span className="dynamic-documents__drawer-title">
              [{configCheckboxBase}] — {ANCHORS.TABLE.CHECKBOX_CONFIG}
            </span>
          ) : null
        }
        destroyOnClose={false}
        rootClassName="dynamic-documents__drawer"
      >
        {configCheckboxBase &&
          (() => {
            const grp = displayItems.find(
              (it) =>
                it.type === "checkboxGroup" && it.base === configCheckboxBase,
            );
            const variants = grp ? grp.variants : [];
            return (
              <div className="dynamic-documents__checkbox-options">
                {variants.map((variant, idx) => {
                  const m = mappings[variant] || {};
                  return (
                    <div key={variant}>
                      <Divider
                        orientation="left"
                        className="dynamic-documents__checkbox-divider"
                      >
                        {t("ANCHORS.CONFIG.OPTION", { index: idx + 1 })}&nbsp;
                        <span className="dynamic-documents__checkbox-option-label">
                          {variant}
                        </span>
                      </Divider>
                      <div className="dynamic-documents__checkbox-option-fields">
                        <ConfigField label={ANCHORS.CONFIG.WIDTH}>
                          <InputNumber
                            min={1}
                            step={1}
                            precision={0}
                            placeholder="10"
                            className="dynamic-documents__field-full"
                            addonAfter="px"
                            value={m.width ?? null}
                            onChange={(val) =>
                              setField(variant, "width", val ?? undefined)
                            }
                          />
                        </ConfigField>
                        <ConfigField label={ANCHORS.CONFIG.HEIGHT}>
                          <InputNumber
                            min={1}
                            step={1}
                            precision={0}
                            placeholder="10"
                            className="dynamic-documents__field-full"
                            addonAfter="px"
                            value={m.height ?? null}
                            onChange={(val) =>
                              setField(variant, "height", val ?? undefined)
                            }
                          />
                        </ConfigField>
                        <ConfigField label={ANCHORS.CONFIG.FILL_COLOR}>
                          <div className="dynamic-documents__color-row">
                            <input
                              type="color"
                              value={m.color || "#000000"}
                              onChange={(e) =>
                                setField(variant, "color", e.target.value)
                              }
                              className="dynamic-documents__color-input"
                            />
                            <span className="dynamic-documents__color-value">
                              {m.color || "#000000"}
                            </span>
                          </div>
                        </ConfigField>
                        <ConditionsEditor
                          conditions={getConditions(variant)}
                          emptyMessage={ANCHORS.CONFIG.CONDITIONS_EMPTY}
                          onOperatorChange={(op) =>
                            setConditionOperator(variant, op)
                          }
                          onAddRule={() => addRule(variant)}
                          onRemoveRule={(i) => removeRule(variant, i)}
                          onUpdateRule={(i, patch) =>
                            updateRule(variant, i, patch)
                          }
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
      </Drawer>

      <div className="pricers-import__card-actions">
        <Button type="primary" onClick={onNext}>
          {ANCHORS.NEXT}
        </Button>
        {onPreview && (
          <>
            <Tooltip title={ANCHORS.PAYLOAD_DRAWER.TITLE}>
              <button
                type="button"
                onClick={() => setPayloadOpen(true)}
                className={classNames("dynamic-documents__preview-data-btn", {
                  "dynamic-documents__preview-data-btn--filled": Object.keys(
                    mockValues,
                  ).some((k) => mockValues[k]),
                })}
              >
                <BiData size={16} />
              </button>
            </Tooltip>
            <Button
              type="primary--outlined"
              onClick={() => onPreview(buildPreviewPayload())}
              loading={previewing}
              className="ml-8"
            >
              {ANCHORS.PREVIEW}
            </Button>
          </>
        )}

        <Tooltip title={CONFIG_IO.IMPORT}>
          <button
            type="button"
            onClick={() => configInputRef.current?.click()}
            className="dynamic-documents__preview-data-btn ml-8"
          >
            <BiUpload size={16} />
          </button>
        </Tooltip>
        <Tooltip title={CONFIG_IO.EXPORT}>
          <button
            type="button"
            onClick={handleExportConfig}
            disabled={Object.keys(mappings).length === 0}
            className="dynamic-documents__preview-data-btn"
          >
            <BiDownload size={16} />
          </button>
        </Tooltip>
        <input
          ref={configInputRef}
          type="file"
          accept=".json,application/json"
          className="dynamic-documents__hidden-input"
          onChange={handleImportConfig}
        />
      </div>
    </Card>
  );
};

const ConfigField = ({ label, hint, children }) => (
  <div>
    <div className="dynamic-documents__config-field-label">{label}</div>
    {children}
    {hint && <div className="dynamic-documents__config-field-hint">{hint}</div>}
  </div>
);

export default StepAnchors;
